import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from './ui/icon'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { useLocale } from '../contexts/LocaleContext'
import {
  useContractClauseAnalysis,
  useContractTypeProperties,
  useContractProperties,
  type ClauseFinding,
  type ClauseVerdict,
  type ContractPropertyValue,
  type PropertyDefinition,
} from '../hooks/useQueries'
import { useContractQualityIssues, type ContractQualityIssue } from '../hooks/useContractQualityIssues'
import { ClauseValueControl } from './ContractClausesTab'
import { EmptyState } from './ui/empty-state'
import { mdiAlert, mdiArrowBottomRight, mdiCancel, mdiCheck, mdiHelpCircleOutline, mdiLightbulbOutline, mdiOpenInNew, mdiRefresh } from '@mdi/js'

// The Improvement tab: the contract held to what its type expects of it.
//
// The Clauses tab shows the contract's own state, clause by clause. It cannot
// show an ECART — that a six-month notice period is worse than the three-month
// reference, that a renewal is uncapped, that a response time is outside the
// trade norm. This screen is about the gaps only, worst first, each with what
// the contract says, what the standard asks and why.

interface ContractImprovementTabProps {
  org: string
  contractUid: string
  contractTypeUid: string
  hasType: boolean
}

// Verdicts that are a gap. `conform` is collapsed by default and
// `indeterminate` is stated separately: neither is work to do.
const GAP_VERDICTS: ClauseVerdict[] = ['forbidden', 'weaker', 'absent']

const VERDICT_ICON: Record<ClauseVerdict, string> = {
  conform: mdiCheck,
  absent: mdiAlert,
  weaker: mdiArrowBottomRight,
  forbidden: mdiCancel,
  indeterminate: mdiHelpCircleOutline,
}

// Which sweep ticket carries which verdict. A gap of LEVEL and a MISSING clause
// are two different tickets by design (one is renegotiated, the other filled
// in), so a row must point at the one that actually carries it rather than at
// "the contract's tickets" in general.
const VERDICT_SUBTYPE: Partial<Record<ClauseVerdict, string>> = {
  weaker: 'contract_below_model',
  forbidden: 'contract_below_model',
  absent: 'contract_clauses_missing',
}

const VERDICT_VARIANT: Record<ClauseVerdict, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  conform: 'success',
  absent: 'warning',
  weaker: 'warning',
  forbidden: 'destructive',
  indeterminate: 'secondary',
}

// Resolve a multilingual map to the active locale (locale → en → fr → first).
function localized(values: Record<string, string> | undefined, locale: string, fallback = ''): string {
  if (!values) return fallback
  return values[locale] || values.en || values.fr || Object.values(values)[0] || fallback
}

type EnumLabels = Record<string, Record<string, string>>
type ValueRenderer = (value: unknown, labels?: EnumLabels) => string

// Render a clause value the way the ticket does: "6 mois", not the stored JSON,
// and an enum code by its name ("Pleinement signé", not fully_executed). The
// unit words come from i18n so the screen reads in the active language.
function useValueRenderer(): ValueRenderer {
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  return (value, labels) => {
    const named = (code: string) => localized(labels?.[code], locale, code)
    if (value === undefined || value === null) return '—'
    if (typeof value === 'string') return named(value)
    if (typeof value === 'number') return String(value)
    if (typeof value === 'boolean') return t(value ? 'improvement.yes' : 'improvement.no')
    if (Array.isArray(value)) return value.map(v => (typeof v === 'string' ? named(v) : JSON.stringify(v))).join(', ')
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>
      if (typeof obj.value === 'number') {
        const unit = typeof obj.unit === 'string' ? obj.unit.toUpperCase() : ''
        if (unit) {
          return t(`improvement.unit.${unit}`, { count: obj.value, defaultValue: `${obj.value} ${unit}` })
        }
        return String(obj.value)
      }
      if (typeof obj.amount === 'number') {
        const currency = typeof obj.currency === 'string' ? obj.currency : ''
        return currency === 'EUR' ? `${obj.amount} €` : `${obj.amount} ${currency}`.trim()
      }
    }
    return JSON.stringify(value)
  }
}

export function ContractImprovementTab({ org, contractUid, contractTypeUid, hasType }: ContractImprovementTabProps) {
  const { t } = useTranslation('contracts')
  const { locale } = useLocale()
  const renderValue = useValueRenderer()
  const [showConform, setShowConform] = useState(false)

  const {
    data: analysis,
    isLoading,
    isError,
    refetch,
  } = useContractClauseAnalysis(org, contractUid, { enabled: hasType && !!contractUid })

  // Same source the Clauses tab edits — findings are keyed by property_uid
  // (== property_definition.uid), so a finding here can be made directly
  // editable rather than sending the reader to the other tab to fix a gap.
  const { data: typeProps = [] } = useContractTypeProperties(org, contractTypeUid, {
    enabled: hasType && !!contractTypeUid,
  })
  const { data: values = [] } = useContractProperties(org, contractUid)
  const defsByUid = useMemo(() => {
    const map = new Map<string, PropertyDefinition>()
    for (const p of typeProps) {
      const def = p.property_definition as PropertyDefinition | undefined
      if (def?.uid) map.set(def.uid, def)
    }
    return map
  }, [typeProps])
  const valuesByDefUid = useMemo(() => {
    const map = new Map<string, ContractPropertyValue>()
    for (const v of values) {
      const uid = (v.property_definition as PropertyDefinition | undefined)?.uid
      if (uid) map.set(uid, v)
    }
    return map
  }, [values])

  // The tickets the contract-quality sweep raised from these very verdicts.
  // Reusing the hook that already feeds the "à fiabiliser" badge rather than
  // querying again: same source, so the screen and the badge cannot disagree.
  const { byContract } = useContractQualityIssues(org)
  const ticketBySubtype = useMemo(() => {
    const map: Record<string, ContractQualityIssue> = {}
    for (const issue of byContract[contractUid] || []) {
      if (issue.subtype && !map[issue.subtype]) map[issue.subtype] = issue
    }
    return map
  }, [byContract, contractUid])

  const gaps = useMemo(
    () => (analysis?.findings || []).filter(f => GAP_VERDICTS.includes(f.verdict)),
    [analysis],
  )
  const conform = useMemo(
    () => (analysis?.findings || []).filter(f => f.verdict === 'conform'),
    [analysis],
  )
  const undetermined = useMemo(
    () => (analysis?.findings || []).filter(f => f.verdict === 'indeterminate'),
    [analysis],
  )

  if (!hasType) {
    return (
      <div className="text-sm text-muted-foreground italic p-6" data-testid="improvement-no-type">
        {t('clauses.no_type')}
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40" data-testid="improvement-loading">
        <div className="motion-safe:animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Same rule as the Clauses tab: a failed request must never read as "nothing
  // to improve" — that is a statement about the contract, not about the network.
  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 p-6" data-testid="improvement-error">
        <p className="text-sm text-destructive">{t('improvement.error')}</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()} data-testid="improvement-retry">
          <AppIcon path={mdiRefresh} size="sm" className="mr-2" />
          {t('clauses.retry')}
        </Button>
      </div>
    )
  }

  const total = (analysis?.findings || []).length
  if (total === 0) {
    return (
      <EmptyState
        variant="inline"
        icon={mdiLightbulbOutline}
        title={t('improvement.no_expectations')}
        data-testid="improvement-empty"
      />
    )
  }

  return (
    <div className="space-y-6" data-testid="improvement-tab">
      <ImprovementHeader
        org={org}
        score={analysis?.score ?? 0}
        gapCount={gaps.length}
        undeterminedCount={undetermined.length}
        tickets={byContract[contractUid] || []}
      />

      {gaps.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground" data-testid="improvement-no-gap">
            {t('improvement.no_gap')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="improvement-gaps">
          {gaps.map(finding => (
            <FindingRow
              key={finding.property_code}
              finding={finding}
              locale={locale}
              renderValue={renderValue}
              org={org}
              contractUid={contractUid}
              def={defsByUid.get(finding.property_uid)}
              current={valuesByDefUid.get(finding.property_uid)}
              ticket={ticketBySubtype[VERDICT_SUBTYPE[finding.verdict] || '']}
            />
          ))}
        </div>
      )}

      {undetermined.length > 0 && (
        <p className="text-sm text-muted-foreground" data-testid="improvement-undetermined">
          {t('improvement.undetermined', { count: undetermined.length })}
        </p>
      )}

      {conform.length > 0 && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowConform(v => !v)}
            data-testid="improvement-toggle-conform"
          >
            {showConform
              ? t('improvement.hide_conform', { count: conform.length })
              : t('improvement.show_conform', { count: conform.length })}
          </Button>
          {showConform && (
            <div className="space-y-3 mt-3" data-testid="improvement-conform">
              {conform.map(finding => (
                <FindingRow
                  key={finding.property_code}
                  finding={finding}
                  locale={locale}
                  renderValue={renderValue}
                  org={org}
                  contractUid={contractUid}
                  def={defsByUid.get(finding.property_uid)}
                  current={valuesByDefUid.get(finding.property_uid)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ImprovementHeader({
  org,
  score,
  gapCount,
  undeterminedCount,
  tickets,
}: {
  org: string
  score: number
  gapCount: number
  undeterminedCount: number
  tickets: ContractQualityIssue[]
}) {
  const { t } = useTranslation('contracts')
  return (
    <Card>
      <CardContent className="p-6 flex flex-wrap items-center gap-6">
        <div>
          <div className="text-3xl font-semibold" data-testid="improvement-score">
            {score}%
          </div>
          <div className="text-xs text-muted-foreground">{t('improvement.score_label')}</div>
        </div>
        <div className="text-sm">
          <div className="font-medium" data-testid="improvement-gap-count">
            {t('improvement.gap_count', { count: gapCount })}
          </div>
          <div className="text-muted-foreground text-xs">
            {t('improvement.score_help', { count: undeterminedCount })}
          </div>
        </div>

        {/* The gaps are already work in Pilotage. Naming the tickets here and
            linking straight to them saves the reader from going to look for
            what this very screen produced. */}
        {tickets.length > 0 && (
          <div className="flex flex-col gap-1 ml-auto" data-testid="improvement-tickets">
            {tickets.map(ticket => (
              <Link
                key={ticket.uid}
                to="/$org/issues/$uid"
                params={{ org, uid: ticket.uid }}
                className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                data-testid={`improvement-ticket-link-${ticket.uid}`}
              >
                <AppIcon path={mdiOpenInNew} size="xs" className="shrink-0" />
                {ticket.title}
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function FindingRow({
  finding,
  locale,
  renderValue,
  org,
  contractUid,
  def,
  current,
  ticket,
}: {
  finding: ClauseFinding
  locale: string
  renderValue: ValueRenderer
  org?: string
  contractUid?: string
  def?: PropertyDefinition
  current?: ContractPropertyValue
  ticket?: ContractQualityIssue
}) {
  const { t } = useTranslation('contracts')
  const verdictIcon = VERDICT_ICON[finding.verdict]
  const name = localized(finding.property_name, locale, finding.property_code)
  const rationale = localized(finding.rationale, locale)

  return (
    <Card data-testid={`improvement-finding-${finding.property_code}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AppIcon path={verdictIcon} size="sm" className="shrink-0" />
            <span className="font-medium truncate">{name}</span>
            {finding.family && (
              <Badge variant="outline" className="shrink-0">
                {finding.family}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {ticket && org && (
              <Link
                to="/$org/issues/$uid"
                params={{ org, uid: ticket.uid }}
                title={ticket.title}
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                data-testid={`improvement-finding-ticket-${finding.property_code}`}
              >
                <AppIcon path={mdiOpenInNew} size="xs" className="shrink-0" />
                {t('improvement.open_ticket')}
              </Link>
            )}
            <Badge
              variant={VERDICT_VARIANT[finding.verdict]}
              data-testid={`improvement-verdict-${finding.property_code}`}
            >
              {t(`improvement.verdict.${finding.verdict}`)}
            </Badge>
          </div>
        </div>

        {/* Two columns: what the contract says / what the standard asks. The
            comparison is the whole point of the screen, so it is laid out as
            one, not buried in a sentence. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground mb-1">{t('improvement.contract_says')}</div>
            <div data-testid={`improvement-actual-${finding.property_code}`}>
              {org && contractUid && def ? (
                <ClauseValueControl org={org} contractUid={contractUid} def={def} current={current} locale={locale} />
              ) : finding.actual === undefined || finding.actual === null ? (
                t('clauses.empty_value')
              ) : (
                renderValue(finding.actual, finding.enum_labels)
              )}
            </div>
          </div>
          <div className="rounded-md border p-3">
            <div className="text-xs text-muted-foreground mb-1">{t('improvement.standard_asks')}</div>
            <div data-testid={`improvement-expected-${finding.property_code}`}>
              {finding.comparator
                ? t(`improvement.comparator.${finding.comparator}`, {
                    value: renderValue(finding.expected, finding.enum_labels),
                    defaultValue: renderValue(finding.expected, finding.enum_labels),
                  })
                : t('improvement.presence_required')}
            </div>
          </div>
        </div>

        {rationale && <p className="text-sm text-muted-foreground">{rationale}</p>}

      </CardContent>
    </Card>
  )
}
